// One trade, in full (TRADES.md "Detail sheet"): the two sides face to
// face, the read-out, the thread of counters, and the actions I have. Used
// inside a DetailSheet on phone and as the right column on tablet and
// desktop. Accepting with a full roster asks for drops first.
//
// A CLEAN SCREEN (eslint.config.js, CLEAN_SCREENS): it draws only through
// the kit, so a look restyles it from a stylesheet (LOOKS.md Part 7). The
// same component for a practice league and a real one; `useTradeActions`
// sends what the manager does to the right place.

import { Fragment, useRef, useState } from 'react';
import type {
  DraftPickRef,
  TradeAction,
  TradePathAction,
  TradeRosterRow,
  TradeStatus,
  TradeView,
} from '@fsp/domain';
import { COMPARE_MAX, valueOf } from '@fsp/domain';
import { useTradeBuilder } from '../../data/hooks';
import type { TradeActions, TradeOutcome } from '../../data/trades';
import { useDelight } from '../../delight/useDelight';
import { GameLabel, PlayerMarks, PositionPill, TeamMark } from '../../kit';
import { Handshake } from '../../kit/Handshake';
import {
  Button,
  Callout,
  Caption,
  Card,
  Chip,
  Line,
  ListRow,
  RowName,
  RowSub,
  Stack,
  Text,
  TextArea,
  Tick,
  tell,
  type Tone,
} from '../../kit/ui';
import { num } from './notComputed';
import { Proj, Worth } from './Projection';
import { PlayerName, TeamName } from './Names';
import { pickShort } from './pickLabel';
import { useAtLeast } from '../../data/detail';
import { useLeagueSource } from '../../data/leagueSource';
import { useCompareTrayIfAny } from '../../shell/compareTray';
import { useChatDrawer } from '../../shell/chatDrawer';

export const STATUS_LABEL: Record<TradeStatus, string> = {
  proposed: 'Offered',
  countered: 'Countered',
  review: 'Under review',
  accepted: 'Cleared',
  executed: 'Done',
  vetoed: 'Vetoed',
  declined: 'Declined',
  withdrawn: 'Withdrawn',
  expired: 'Expired',
  void: 'Void',
};

export function statusTone(s: TradeStatus): Tone {
  if (s === 'executed') return 'good';
  if (s === 'proposed' || s === 'review' || s === 'accepted') return 'warn';
  if (s === 'vetoed' || s === 'declined' || s === 'void') return 'bad';
  return 'neutral';
}

function SideRows({ rows }: { rows: TradeRosterRow[] }) {
  // HIS SEASON BESIDE HIS WORTH (Standard's numbers, as on the Players
  // page): the worth the read-out sums on the row's face (B-185), and under
  // it what he has scored, so a manager can see what it came from.
  const standard = useAtLeast('standard');
  if (rows.length === 0)
    return (
      <ListRow>
        <Text tone="secondary">nothing</Text>
      </ListRow>
    );
  return (
    <>
      {rows.map((r) => (
        <ListRow
          key={r.player.id}
          lead={<PositionPill pos={r.player.positions[0]!} size="sm" />}
          trail={
            <Text tone="secondary" size="xs" numeric>
              {r.worth !== null ? (
                <Worth>{num(r.worth)}</Worth>
              ) : r.projection !== null ? (
                <Proj>{num(r.projection)}</Proj>
              ) : null}
            </Text>
          }
        >
          <RowName marks={<PlayerMarks player={r.player} />}>
            <PlayerName player={r.player} />
          </RowName>
          <RowSub>
            <GameLabel game={r.game} />
            {r.locked ? ' · locked' : null}
          </RowSub>
          {standard && r.seasonAvg !== null ? (
            <RowSub>
              <span className="fsp-num">{r.seasonAvg.toFixed(1)}</span> a game
              {r.seasonPoints !== null ? (
                <>
                  {' · '}
                  <span className="fsp-num">{r.seasonPoints.toFixed(1)}</span> this season
                </>
              ) : null}
            </RowSub>
          ) : null}
        </ListRow>
      ))}
    </>
  );
}

/** What a trade's new state says to the manager who moved it. */
function doneSentence(
  action: TradePathAction,
  status: TradeStatus | null,
  incoming: TradeRosterRow[],
  /** The trade's own sentence once void, when the host sent the trade back. */
  voidSentence: string | null = null,
  /** The draft picks coming in (§2.6). */
  incomingPicks: readonly DraftPickRef[] = [],
): string {
  const all = [...incoming.map((p) => p.player.shortName), ...incomingPicks.map(pickShort)];
  const names = all.join(', ') || 'The players';
  if (status === 'executed') return `Done. ${names} ${all.length === 1 ? 'is' : 'are'} yours.`;
  // A VOID SAYS SO (the trade review's finding 11): accepting an offer whose
  // player had gone said a green *"Done."*.
  if (status === 'void') return voidSentence ? `Void: ${voidSentence}` : 'That trade is void.';
  if (action === 'accept' && status === 'review')
    return 'Accepted. It goes through when the review window ends, unless it is vetoed.';
  if (status === 'accepted') return "Cleared. It executes after this week's games.";
  if (action === 'decline') return 'Declined.';
  if (action === 'withdraw') return 'Offer taken back.';
  if (action === 'vote') return 'Your veto is in.';
  if (action === 'approve') return 'Approved.';
  if (action === 'veto') return 'Vetoed.';
  return 'Done.';
}

/** The trade's status after an action, from whichever host answered. */
function statusAfter(outcome: TradeOutcome & { ok: true }): TradeStatus | null {
  if (outcome.trade !== null) return outcome.trade.status;
  switch (outcome.event) {
    case 'executed':
      return 'executed';
    case 'review.started':
      return 'review';
    case 'accepted':
      return 'accepted';
    case 'void':
      return 'void';
    default:
      return null;
  }
}

export function TradeDetail({
  trade,
  actions,
  onCounter,
  onDone,
}: {
  trade: TradeView;
  actions: TradeActions;
  /** Open the builder pre-filled with the sides swapped. */
  onCounter: (trade: TradeView) => void;
  /** After an action lands (accept, decline, ...). */
  onDone?: () => void;
}) {
  const { play } = useDelight();
  const [vetoReason, setVetoReason] = useState('');
  const [vetoOpen, setVetoOpen] = useState(false);
  const [drops, setDrops] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [proposerSide, receiverSide] = trade.sides;
  const mySide = trade.isMine ? (trade.isProposer ? proposerSide : receiverSide) : proposerSide;
  const theirSide = mySide === proposerSide ? receiverSide : proposerSide;
  const accepting = trade.needsDropsOnAccept !== null && trade.actions.includes('accept');
  // The drop picker for an accept that would leave me over a limit: the
  // builder's own drop options, from my side of this trade, and ITS JUDGEMENT
  // OF THE DROPS PICKED. Counting the sides asked for two drops where an open
  // spot made one enough, and knew nothing of a position's limit (the trade
  // review of 2026-10-22).
  const builder = useTradeBuilder(
    accepting
      ? {
          partnerTeamId: theirSide.team.id,
          give: mySide.players.map((p) => p.player.id),
          get: theirSide.players.map((p) => p.player.id),
          givePicks: [...mySide.picks],
          getPicks: [...theirSide.picks],
          myDrops: drops,
          counterOf: null,
        }
      : null,
  );
  // The last answer stays on screen while the next one loads, as the
  // builder's does: a tick asks again, and the list folding away under the
  // finger moved Decline up into its place (the review of chunk 1).
  const fresh = builder ? valueOf(builder) : null;
  const held = useRef<typeof fresh>(null);
  if (fresh) held.current = fresh;
  const judged = fresh ?? held.current;
  const dropOptions = (judged?.myDropOptions ?? []).filter((o) => o.droppable);
  // A drop can put right a roster over its size or a position's limit, and
  // nothing else: a starting lineup short a position is said, with no picker.
  const picking = accepting && (judged?.mineNeedsDrop ?? true);
  // Ready once the server's builder, asked with these drops, says the roster
  // is legal — the answer to this question, never the one before it.
  const dropsReady = !accepting || (fresh !== null && fresh.problem === null);

  const act = (action: TradePathAction) => {
    if (busy) return;
    setError(null);
    setBusy(true);
    void actions
      .answer(trade.id, action, {
        drops: action === 'accept' ? drops : [],
        reason: action === 'veto' ? vetoReason.trim() : null,
        veto: true,
      })
      .then((outcome) => {
        setBusy(false);
        if (!outcome.ok) {
          setError(outcome.message);
          return;
        }
        const status = statusAfter(outcome);
        if (status !== 'void') play(status === 'executed' ? 'claimWon' : 'lock');
        tell(
          doneSentence(
            action,
            status,
            theirSide.players,
            outcome.trade?.sentence ?? null,
            theirSide.draftPicks,
          ),
          status === 'void'
            ? 'bad'
            : action === 'decline' || action === 'veto'
              ? 'neutral'
              : 'good',
        );
        onDone?.();
      });
  };
  const has = (a: TradeAction) => trade.actions.includes(a);
  // TALK IT OVER (I8): a trade of mine with a person, whom Message writes to.
  const chat = useChatDrawer();
  const writeTo = trade.isMine ? chat.whoToMessage(theirSide.team.id) : null;
  const message = async () => {
    if (writeTo === null) return;
    const done = await chat.message(theirSide.team.id);
    if (!done.ok) tell(done.message, 'bad');
  };
  const tray = useCompareTrayIfAny();
  const real = useLeagueSource() === 'api';
  const everyone = [...theirSide.players, ...mySide.players].map((p) => p.player);
  const compare =
    tray !== null && real && everyone.length >= 2
      ? () => {
          tray.set(everyone.slice(0, COMPARE_MAX).map((p) => ({ id: p.id, name: p.name })));
          tray.open(everyone.slice(0, COMPARE_MAX).map((p) => p.id));
        }
      : null;

  // SHOOK ON IT (PLAN-2026-10-31-a-handshake-a-dance-and-the-photo §1): once
  // it has gone through, or cleared its review and waits to run.
  const shook = trade.status === 'executed' || trade.status === 'accepted';

  return (
    <Stack gap={4}>
      {shook ? (
        <div className="fsp-trade-shook">
          <Handshake left={mySide.team} right={theirSide.team} />
          <Text size="sm" tone="secondary" block>
            {trade.isMine
              ? `You and ${theirSide.team.name} shook on it.`
              : `${mySide.team.name} and ${theirSide.team.name} shook on it.`}
          </Text>
        </div>
      ) : null}
      <Line spread align="start" gap={3}>
        <Text block>{trade.sentence}</Text>
        <Chip tone={statusTone(trade.status)}>{STATUS_LABEL[trade.status]}</Chip>
      </Line>
      <Text tone="secondary" size="xs" block>
        {trade.timeLabel}
        {trade.status === 'review' && trade.votes.needed
          ? ` · ${trade.votes.vetoes} of ${trade.votes.needed} vetoes`
          : ''}
      </Text>

      <div className="fsp-trade-sides">
        {[mySide, theirSide].map((side) => (
          <Card key={side.team.id}>
            <div className="fsp-trade-side-head">
              <TeamMark team={side.team} size={22} />
              <span>
                {side === mySide && trade.isMine ? (
                  'You give'
                ) : (
                  <>
                    <TeamName team={side.team}>{side.team.ownerName}</TeamName> gives
                  </>
                )}
              </span>
              {side === theirSide && writeTo !== null ? (
                <span className="fsp-trade-side-message">
                  <Button
                    size="xs"
                    variant="quiet"
                    icon="chat"
                    label={`Message ${writeTo}`}
                    onClick={() => void message()}
                  >
                    Message
                  </Button>
                </span>
              ) : null}
            </div>
            <SideRows rows={side.players} />
            {/* THE DRAFT PICKS, each named, with what it counts (§2.6). */}
            {side.draftPicks.map((p) => (
              <ListRow
                key={p.id}
                trail={
                  p.worth !== null ? (
                    <Text tone="secondary" size="xs" numeric>
                      <Worth>{num(p.worth)}</Worth>
                    </Text>
                  ) : undefined
                }
              >
                <RowName>{p.name}</RowName>
              </ListRow>
            ))}
            {side.drops.length ? (
              <ListRow>
                <Text tone="secondary" size="xs">
                  and drops{' '}
                  {side.drops.map((d, i) => (
                    <Fragment key={d.id}>
                      {i > 0 ? ', ' : ''}
                      <PlayerName player={d} look="inline" club={d.proTeam} />
                    </Fragment>
                  ))}
                </Text>
              </ListRow>
            ) : null}
          </Card>
        ))}
      </div>

      {/* EVERY PLAYER IN IT, SIDE BY SIDE (B-375): the tray filled with
          them, and Compare opened, the players I would get first. */}
      {compare !== null ? (
        <Line>
          <Button size="xs" icon="compare" onClick={compare}>
            Compare these players
          </Button>
        </Line>
      ) : null}

      {/* No read-out in a league that turned it off (RULES_SCHEMA §15c). */}
      {trade.readout === null ? null : (
        <Card padded>
          <Stack gap={1}>
            <Caption>{trade.isMine ? 'For you' : `For ${trade.proposer.ownerName}`}</Caption>
            {trade.readout.sentences.map((s, i) => (
              <Text key={i} block>
                {s}
              </Text>
            ))}
          </Stack>
        </Card>
      )}

      {trade.thread.length > 1 || trade.note ? (
        <Stack gap={1}>
          <Caption>Thread</Caption>
          {trade.thread.map((t) => (
            <div key={t.tradeId} className="fsp-trade-thread-item">
              <Text weight="strong">{t.by}</Text>
              <Text tone="secondary" size="xs">
                {' '}
                · {t.summary}
              </Text>
              {t.note ? <Text block>“{t.note}”</Text> : null}
            </div>
          ))}
        </Stack>
      ) : null}

      {accepting ? (
        <Stack gap={2}>
          {picking ? <Caption>Drop someone to make room</Caption> : null}
          {dropsReady ? null : (
            <Text tone="secondary" size="xs" block>
              {judged?.problem ?? trade.needsDropsOnAccept}
            </Text>
          )}
          {picking ? (
            <Card>
              {dropOptions.map((o) => {
                const on = drops.includes(o.player.id);
                return (
                  <ListRow
                    key={o.player.id}
                    lead={<Tick on={on} />}
                    leadSize="sm"
                    checked={on}
                    selected={on}
                    onClick={() =>
                      setDrops((d) =>
                        on ? d.filter((x) => x !== o.player.id) : [...d, o.player.id],
                      )
                    }
                    trail={
                      // None in a league that keeps no projections (§15c).
                      o.projection !== null ? (
                        <Text tone="secondary" size="xs" numeric>
                          <Proj>{num(o.projection)}</Proj>
                        </Text>
                      ) : undefined
                    }
                  >
                    <RowName marks={<PlayerMarks player={o.player} />}>{o.player.name}</RowName>
                    <RowSub>
                      {o.slot} · <GameLabel game={o.game} />
                      {o.locked ? ' · game started: the trade waits for the week to end' : ''}
                    </RowSub>
                  </ListRow>
                );
              })}
            </Card>
          ) : null}
        </Stack>
      ) : null}

      {vetoOpen ? (
        <TextArea
          label="Why?"
          description="The league reads this in the log and the chat."
          value={vetoReason}
          onChange={setVetoReason}
          maxLength={300}
        />
      ) : null}

      {error ? <Callout tone="bad">{error}</Callout> : null}

      {trade.actions.length ? (
        <Line gap={2} wrap>
          {has('accept') ? (
            <Button variant="primary" onClick={() => act('accept')} disabled={busy || !dropsReady}>
              Accept
            </Button>
          ) : null}
          {has('counter') ? (
            <Button onClick={() => onCounter(trade)} disabled={busy}>
              Counter
            </Button>
          ) : null}
          {has('decline') ? (
            <Button variant="danger-quiet" onClick={() => act('decline')} disabled={busy}>
              Decline
            </Button>
          ) : null}
          {has('withdraw') ? (
            <Button onClick={() => act('withdraw')} disabled={busy}>
              Take it back
            </Button>
          ) : null}
          {has('vote') ? (
            <Button variant="danger-quiet" onClick={() => act('vote')} disabled={busy}>
              Vote to veto
            </Button>
          ) : null}
          {has('approve') ? (
            <Button variant="primary" onClick={() => act('approve')} disabled={busy}>
              Approve now
            </Button>
          ) : null}
          {has('veto') ? (
            vetoOpen ? (
              <Button
                variant="danger"
                onClick={() => act('veto')}
                disabled={busy || !vetoReason.trim()}
              >
                Veto
              </Button>
            ) : (
              <Button variant="danger-quiet" onClick={() => setVetoOpen(true)}>
                Veto…
              </Button>
            )
          ) : null}
        </Line>
      ) : null}
    </Stack>
  );
}
